import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useAuth } from '../auth/ContextoAuth'
import { clientes as apiClientes, ventas as apiVentas } from '../api/servicios'
import { fechaHora, dinero } from '../api/formato'
import type { Cliente } from '../api/tipos'
import { Dialogo } from '../componentes/Dialogo'
import { Estado } from '../componentes/Estado'
import { Paginador, usarRetardo } from '../componentes/Paginador'
import { mensajeDeError, usarCargar } from '../componentes/usarCargar'
import './listados.css'

type Ventana =
    | { tipo: 'ninguno' }
    | { tipo: 'nuevo' }
    | { tipo: 'editar'; cliente: Cliente }
    | { tipo: 'cuenta'; cliente: Cliente }

export function Clientes() {
    const { puede } = useAuth()
    const puedeEditar = puede('catalogo.editar')

    const [texto, setTexto] = useState('')
    const [soloConDeuda, setSoloConDeuda] = useState(false)
    const [pagina, setPagina] = useState(1)
    const [ventana, setVentana] = useState<Ventana>({ tipo: 'ninguno' })

    const busqueda = usarRetardo(texto)

    const clientes = usarCargar(
        () =>
            apiClientes.listar({
                texto: busqueda || undefined,
                soloConDeuda,
                pagina,
            }),
        [busqueda, soloConDeuda, pagina],
    )

    return (
        <div className="pagina-listado columna g4">
            <header className="fila entre envuelve g3">
                <h1>Clientes y crédito</h1>

                {puedeEditar && (
                    <button
                        type="button"
                        className="boton boton-primario"
                        onClick={() => setVentana({ tipo: 'nuevo' })}
                    >
                        Nuevo cliente
                    </button>
                )}
            </header>

            <section className="panel columna g3">
                <div className="filtros">
                    <input
                        className="campo filtro-ancho"
                        type="search"
                        placeholder="Buscar por nombre, RUT o teléfono"
                        value={texto}
                        onChange={(e) => {
                            setTexto(e.target.value)
                            setPagina(1)
                        }}
                    />

                    <label className="fila g2 interruptor">
                        <input
                            type="checkbox"
                            checked={soloConDeuda}
                            onChange={(e) => {
                                setSoloConDeuda(e.target.checked)
                                setPagina(1)
                            }}
                        />
                        <span>Solo con deuda</span>
                    </label>
                </div>

                <Estado
                    cargando={clientes.cargando}
                    error={clientes.error}
                    alReintentar={clientes.recargar}
                    vacio={(clientes.datos?.items ?? []).length === 0}
                    mensajeVacio={
                        soloConDeuda
                            ? 'Ningún cliente tiene deuda pendiente.'
                            : 'No hay clientes registrados.'
                    }
                >
                    <div className="tabla-envoltura">
                        <table className="tabla">
                            <thead>
                                <tr>
                                    <th>Cliente</th>
                                    <th className="oculto-angosto">Teléfono</th>
                                    <th className="derecha">Deuda</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {(clientes.datos?.items ?? []).map((c) => (
                                    <tr key={c.clienteId}>
                                        <td>
                                            <div className="columna">
                                                <span>{c.nombreCompleto}</span>
                                                <span className="tenue diminuto numero">
                                                    {c.numeroDocumento ?? 'sin RUT'}
                                                    {!c.activo && ' · inactivo'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="oculto-angosto numero chico">
                                            {c.telefono ?? '—'}
                                        </td>
                                        <td
                                            className={`derecha numero ${
                                                c.saldoCredito > 0 ? 'monto-resta' : ''
                                            }`}
                                        >
                                            {dinero(c.saldoCredito)}
                                        </td>
                                        <td className="derecha">
                                            <div className="fila g2 acciones-fila">
                                                <button
                                                    type="button"
                                                    className="boton boton-fantasma"
                                                    onClick={() =>
                                                        setVentana({ tipo: 'cuenta', cliente: c })
                                                    }
                                                >
                                                    Cuenta
                                                </button>
                                                {puedeEditar && (
                                                    <button
                                                        type="button"
                                                        className="boton boton-fantasma"
                                                        onClick={() =>
                                                            setVentana({ tipo: 'editar', cliente: c })
                                                        }
                                                    >
                                                        Editar
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Estado>

                <Paginador pagina={clientes.datos} alCambiar={setPagina} />
            </section>

            {(ventana.tipo === 'nuevo' || ventana.tipo === 'editar') && (
                <DialogoCliente
                    cliente={ventana.tipo === 'editar' ? ventana.cliente : null}
                    alGuardar={() => {
                        setVentana({ tipo: 'ninguno' })
                        clientes.recargar()
                    }}
                    alCerrar={() => setVentana({ tipo: 'ninguno' })}
                />
            )}

            {ventana.tipo === 'cuenta' && (
                <DialogoCuenta
                    cliente={ventana.cliente}
                    alCerrar={() => {
                        setVentana({ tipo: 'ninguno' })
                        clientes.recargar()
                    }}
                />
            )}
        </div>
    )
}

/* -------------------------------------------------------------------------- */
/* Alta y edicion                                                              */
/* -------------------------------------------------------------------------- */

function DialogoCliente({
    cliente,
    alGuardar,
    alCerrar,
}: {
    cliente: Cliente | null
    alGuardar: () => void
    alCerrar: () => void
}) {
    const [rut, setRut] = useState(cliente?.numeroDocumento ?? '')
    const [nombres, setNombres] = useState(cliente?.nombres ?? '')
    const [apellidos, setApellidos] = useState(cliente?.apellidos ?? '')
    const [telefono, setTelefono] = useState(cliente?.telefono ?? '')
    const [direccion, setDireccion] = useState(cliente?.direccion ?? '')
    const [activo, setActivo] = useState(cliente?.activo ?? true)
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    async function guardar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (nombres.trim().length === 0) {
            setError('El nombre es obligatorio.')
            return
        }

        setEnviando(true)

        try {
            if (cliente) {
                await apiClientes.actualizar(cliente.clienteId, {
                    nombres: nombres.trim(),
                    apellidos: apellidos.trim() || undefined,
                    telefono: telefono.trim() || undefined,
                    direccion: direccion.trim() || undefined,
                    activo,
                })
            } else {
                await apiClientes.crear({
                    rut: rut.trim() || undefined,
                    nombres: nombres.trim(),
                    apellidos: apellidos.trim() || undefined,
                    telefono: telefono.trim() || undefined,
                    direccion: direccion.trim() || undefined,
                })
            }

            alGuardar()
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo={cliente ? 'Editar cliente' : 'Nuevo cliente'}
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-cliente"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Guardando…' : 'Guardar'}
                    </button>
                </>
            }
        >
            <form id="forma-cliente" className="columna g4" onSubmit={guardar}>
                {!cliente && (
                    <div>
                        <label className="etiqueta" htmlFor="rut">
                            RUT <span className="tenue">(opcional)</span>
                        </label>
                        <input
                            id="rut"
                            className="campo numero"
                            maxLength={20}
                            placeholder="12.345.678-9"
                            value={rut}
                            onChange={(e) => setRut(e.target.value)}
                        />
                        <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                            Se valida el dígito verificador. No se puede cambiar después.
                        </p>
                    </div>
                )}

                <div>
                    <label className="etiqueta" htmlFor="nombres">
                        Nombres
                    </label>
                    <input
                        id="nombres"
                        className="campo"
                        maxLength={120}
                        value={nombres}
                        onChange={(e) => setNombres(e.target.value)}
                    />
                </div>

                <div>
                    <label className="etiqueta" htmlFor="apellidos">
                        Apellidos <span className="tenue">(opcional)</span>
                    </label>
                    <input
                        id="apellidos"
                        className="campo"
                        maxLength={120}
                        value={apellidos}
                        onChange={(e) => setApellidos(e.target.value)}
                    />
                </div>

                <div>
                    <label className="etiqueta" htmlFor="telefono">
                        Teléfono <span className="tenue">(opcional)</span>
                    </label>
                    <input
                        id="telefono"
                        className="campo numero"
                        type="tel"
                        maxLength={30}
                        value={telefono}
                        onChange={(e) => setTelefono(e.target.value)}
                    />
                </div>

                <div>
                    <label className="etiqueta" htmlFor="direccion">
                        Dirección <span className="tenue">(opcional)</span>
                    </label>
                    <input
                        id="direccion"
                        className="campo"
                        maxLength={250}
                        value={direccion}
                        onChange={(e) => setDireccion(e.target.value)}
                    />
                </div>

                {cliente && (
                    <label className="fila g2 interruptor">
                        <input
                            type="checkbox"
                            checked={activo}
                            onChange={(e) => setActivo(e.target.checked)}
                        />
                        <span>Activo</span>
                    </label>
                )}

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}

/* -------------------------------------------------------------------------- */
/* Estado de cuenta                                                            */
/* -------------------------------------------------------------------------- */

function DialogoCuenta({ cliente, alCerrar }: { cliente: Cliente; alCerrar: () => void }) {
    const { puede } = useAuth()
    const cuenta = usarCargar(() => apiClientes.estadoCuenta(cliente.clienteId), [cliente.clienteId])

    const [abonando, setAbonando] = useState(false)

    return (
        <Dialogo
            titulo={`Cuenta · ${cliente.nombreCompleto}`}
            ancho="ancho"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cerrar
                    </button>

                    {puede('credito.abono') && (cuenta.datos?.saldo ?? 0) > 0 && (
                        <button
                            type="button"
                            className="boton boton-primario"
                            onClick={() => setAbonando(true)}
                        >
                            Registrar abono
                        </button>
                    )}
                </>
            }
        >
            <Estado cargando={cuenta.cargando} error={cuenta.error} alReintentar={cuenta.recargar}>
                {cuenta.datos && (
                    <div className="columna g4">
                        {abonando && (
                            <FormularioAbono
                                cliente={cliente}
                                saldo={cuenta.datos.saldo}
                                alRegistrado={() => {
                                    setAbonando(false)
                                    cuenta.recargar()
                                }}
                                alCancelar={() => setAbonando(false)}
                            />
                        )}

                        <div className="tarjetas-cifra">
                            <div className="tarjeta-cifra">
                                <span className="etiqueta">Deuda</span>
                                <span className="cifra">{dinero(cuenta.datos.saldo)}</span>
                            </div>
                            <div className="tarjeta-cifra">
                                <span className="etiqueta">Cargos</span>
                                <span className="cifra">{dinero(cuenta.datos.totalCargos)}</span>
                            </div>
                            <div className="tarjeta-cifra">
                                <span className="etiqueta">Abonos</span>
                                <span className="cifra">{dinero(cuenta.datos.totalAbonos)}</span>
                            </div>
                        </div>

                        {cuenta.datos.movimientos.length === 0 ? (
                            <p className="vacio">Este cliente no tiene movimientos de crédito.</p>
                        ) : (
                            <div className="tabla-envoltura">
                                <table className="tabla">
                                    <thead>
                                        <tr>
                                            <th>Fecha</th>
                                            <th>Movimiento</th>
                                            <th className="derecha">Importe</th>
                                            <th className="derecha">Saldo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cuenta.datos.movimientos.map((m) => (
                                            <tr key={m.movimientoCreditoClienteId}>
                                                <td className="chico">{fechaHora(m.registradoEn)}</td>
                                                <td>
                                                    <div className="columna">
                                                        <span className="chico">{m.tipo}</span>
                                                        <span className="tenue diminuto">
                                                            {m.ventaNumero ??
                                                                m.medioPagoCodigo ??
                                                                m.observacion ??
                                                                ''}
                                                            {m.estado === 'Anulada' && ' · anulado'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td
                                                    className={`derecha numero ${
                                                        m.importeConSigno > 0
                                                            ? 'monto-resta'
                                                            : 'monto-suma'
                                                    }`}
                                                >
                                                    {m.importeConSigno > 0 ? '+' : '−'}
                                                    {dinero(m.importe)}
                                                </td>
                                                <td className="derecha numero">
                                                    {dinero(m.saldoAcumulado)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </Estado>

        </Dialogo>
    )
}

/**
 * Abono dentro del mismo dialogo de la cuenta, no en uno encima del otro: dos
 * dialogos anidados se pelean por la tecla Escape y por el foco.
 */
function FormularioAbono({
    cliente,
    saldo,
    alRegistrado,
    alCancelar,
}: {
    cliente: Cliente
    saldo: number
    alRegistrado: () => void
    alCancelar: () => void
}) {
    const [importe, setImporte] = useState(String(saldo))

    // Los medios los da el servidor: la lista escrita a mano solo tenia
    // efectivo y debito, y con los identificadores asumidos.
    const medios = usarCargar(() => apiVentas.mediosPago(), [])
    const disponibles = medios.datos ?? []

    const [medioPagoId, setMedioPagoId] = useState('')
    const [referencia, setReferencia] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    const medio = disponibles.find((m) => String(m.medioPagoId) === medioPagoId)

    // Se elige el primero en cuanto llega la lista, para que el desplegable
    // nunca quede en blanco.
    useEffect(() => {
        if (medioPagoId === '' && disponibles.length > 0) {
            setMedioPagoId(String(disponibles[0]!.medioPagoId))
        }
    }, [medioPagoId, disponibles])

    async function registrar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        const valor = Number(importe)

        if (!Number.isFinite(valor) || valor <= 0) {
            setError('Ingrese un importe mayor que cero.')
            return
        }

        if (valor > saldo) {
            setError(`El abono no puede superar la deuda de ${dinero(saldo)}.`)
            return
        }

        if (medio?.requiereReferencia && referencia.trim().length === 0) {
            setError(`${medio.nombre} exige el número de operación.`)
            return
        }

        setEnviando(true)

        try {
            await apiClientes.registrarAbono(cliente.clienteId, {
                importe: Math.round(valor),
                medioPagoId: Number(medioPagoId),
                referencia: referencia.trim() || undefined,
            })

            alRegistrado()
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <form className="panel columna g3" onSubmit={registrar}>
            <h3>Registrar abono</h3>

            <p className="tenue chico">
                El abono entra a la caja abierta y aparece en el arqueo del turno.
            </p>

            <div className="filtros">
                <div>
                    <label className="etiqueta" htmlFor="importe-abono">
                        Importe <span className="tenue">(deuda: {dinero(saldo)})</span>
                    </label>
                    <input
                        id="importe-abono"
                        className="campo campo-numero"
                        type="number"
                        min={1}
                        step={1}
                        inputMode="numeric"
                        value={importe}
                        onChange={(e) => setImporte(e.target.value)}
                    />
                </div>

                <div>
                    <label className="etiqueta" htmlFor="medio-abono">
                        Medio de pago
                    </label>
                    <select
                        id="medio-abono"
                        className="campo"
                        value={medioPagoId}
                        onChange={(e) => setMedioPagoId(e.target.value)}
                    >
                        {disponibles.map((m) => (
                            <option key={m.medioPagoId} value={m.medioPagoId}>
                                {m.nombre}
                            </option>
                        ))}
                    </select>
                </div>

                {medio?.requiereReferencia && (
                    <div>
                        <label className="etiqueta" htmlFor="referencia-abono">
                            N° de operación
                        </label>
                        <input
                            id="referencia-abono"
                            className="campo numero"
                            maxLength={100}
                            value={referencia}
                            onChange={(e) => setReferencia(e.target.value)}
                        />
                    </div>
                )}
            </div>

            {error && (
                <div className="aviso aviso-error" role="alert">
                    {error}
                </div>
            )}

            <div className="fila g2">
                <button type="button" className="boton" onClick={alCancelar}>
                    Cancelar
                </button>
                <button type="submit" className="boton boton-primario" disabled={enviando}>
                    {enviando ? 'Registrando…' : 'Registrar abono'}
                </button>
            </div>
        </form>
    )
}
